import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  CircularProgress,
  Paper,
  Button,
  Pagination,
  // SelectChangeEvent,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FactList from '../components/facts/FactList';
import { RootState, AppDispatch } from '../store';
import { triviaApi } from '../services/api';
import { Fact } from '../types';
import { fetchAllCategories } from '../store/slices/categoriesSlice';

const CategoryDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const categories = useSelector(
    (state: RootState) => state.categories.categories
  );
  const categoriesLoading = useSelector(
    (state: RootState) => state.categories.loading
  );

  const [facts, setFacts] = useState<Fact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 分頁狀態
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // const [itemsPerPage, setItemsPerPage] = useState(6);
  // const [sortBy, setSortBy] = useState<string>('date');

  // 確保類別已載入
  useEffect(() => {
    if (categories.length === 0 && !categoriesLoading) {
      dispatch(fetchAllCategories());
    }
  }, [categories.length, categoriesLoading, dispatch]);

  // 查找當前類別
  const category = categories.find((cat) => cat.slug === slug);

  // 獲取類別的冷知識
  useEffect(() => {
    const fetchCategoryFacts = async () => {
      // 如果類別還未載入，不進行操作
      if (categories.length === 0) {
        return;
      }

      // 如果找不到該類別，設置錯誤
      if (!category) {
        setError('找不到該類別');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log(`正在獲取類別 ID ${category.id} 的冷知識...`);

        // 使用API獲取該類別的冷知識
        // 在實際實現中，添加分頁參數
        // 例如: const categoryFacts = await triviaApi.getTriviaByCategory(category.id.toString(), page, itemsPerPage, sortBy);

        // const result = await triviaApi.getTriviaByCategory(
        //   category.id.toString(),
        //   page,
        //   itemsPerPage,
        //   sortBy
        // );
        const result = await triviaApi.getTriviaByCategory(
          category.id.toString(),
          1, // 固定第一頁
          3, // 每個類別只顯示3條
          'date' // 最新的
        );

        setFacts(result.facts);
        setTotalPages(1);
        setLoading(false);
      } catch (err) {
        console.error(`獲取 ${category.name} 類別的冷知識失敗:`, err);
        setError(`無法載入 ${category.name} 類別的冷知識`);
        setLoading(false);
      }
    };

    fetchCategoryFacts();
  }, [category, categories, slug, page]);
  // }, [category, categories, slug, page, itemsPerPage, sortBy]);

  //
  // 處理分頁變更
  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    // 滾動到頁面頂部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // // 處理每頁條目數變更
  // const handleItemsPerPageChange = (event: SelectChangeEvent) => {
  //   const newItemsPerPage = parseInt(event.target.value);
  //   setItemsPerPage(newItemsPerPage);
  //   setPage(1); // 重置到第一頁
  // };

  // // 處理排序方式變更
  // const handleSortChange = (event: SelectChangeEvent) => {
  //   setSortBy(event.target.value);
  // };

  // 載入中狀態 - 當類別列表仍在載入時
  if (categoriesLoading) {
    return (
      <Container sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            正在載入類別...
          </Typography>
        </Box>
      </Container>
    );
  }

  // 找不到類別
  if (!category && !loading && categories.length > 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            找不到該類別
          </Typography>
          <Button
            component={Link}
            to="/categories"
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
          >
            返回所有類別
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      {/* 麵包屑導航 */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          首頁
        </Link>
        <Link
          to="/categories"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          類別
        </Link>
        <Typography color="text.primary">{category?.name || slug}</Typography>
      </Breadcrumbs>
      <Typography variant="h4" component="h1" gutterBottom>
        {category?.name || '載入中...'}
      </Typography>
      {category?.description && (
        <Typography variant="body1" paragraph>
          {category.description}
        </Typography>
      )}
      {/* 篩選和排序控制 */}
      {/* {!error && facts.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
          >
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="sort-label">排序方式</InputLabel>
              <Select
                labelId="sort-label"
                value={sortBy}
                label="排序方式"
                onChange={handleSortChange}
              >
                <MenuItem value="date">最新</MenuItem>
                <MenuItem value="likes">最多讚</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="items-per-page-label">每頁顯示</InputLabel>
              <Select
                labelId="items-per-page-label"
                value={itemsPerPage.toString()}
                label="每頁顯示"
                onChange={handleItemsPerPageChange}
              >
                <MenuItem value="3">3 條</MenuItem>
                <MenuItem value="6">6 條</MenuItem>
                <MenuItem value="9">9 條</MenuItem>
                <MenuItem value="12">12 條</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />

            <Typography variant="body2" color="text.secondary">
              總計 {facts.length * totalPages} 條
            </Typography>
          </Stack>
        </Paper>
      )} */}
      {error ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
          <Button
            component={Link}
            to="/categories"
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
          >
            返回所有類別
          </Button>
        </Paper>
      ) : (
        <>
          <FactList
            facts={facts}
            loading={loading}
            emptyMessage={`暫無 ${category?.name || ''} 類別的冷知識`}
          />

          {/* 分頁控制 */}
          {totalPages > 1 && !loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}{' '}
      {/* 引導提示 */}
      <Paper
        sx={{
          p: 3,
          mt: 4,
          bgcolor: 'primary.light',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" gutterBottom>
          想探索更多冷知識？
        </Typography>
        <Typography variant="body1" paragraph>
          每天使用"每日冷知識"功能，或嘗試"換一則"按鈕發現更多有趣內容！
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          component={Link}
          to="/"
          size="large"
        >
          返回首頁
        </Button>
      </Paper>
    </Container>
  );
};

export default CategoryDetailPage;
